import { useEffect, useState, useRef } from "react";
import ActivityLogItem from "../../components/ActivityLogItem";
import OverviewChart from "../../components/OverviewChart";
import StatCard from "../../components/StatCard";
import { Button, message, Spin, Alert } from "antd";
import { useBasePath } from "../../components/NavigatePath";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";

const Dashboard = () => {
  const API_URL = import.meta.env.VITE_API_URL;
  const accessToken = useSelector((state) => state.user.accessToken);
  const user = useSelector((state) => state.user.user);
  const pharmacyId = useSelector((state) => state.user.pharmacyDetails);
  const [activityLogs, setActivityLogs] = useState([]);
  const [Statistics, setStatistics] = useState([]);
  const userType = Number(user.roleDetails.id);
  const [isLoading, setIsLoading] = useState(true);
  const [logsLoading, setLogsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const logsContainerRef = useRef(null);
  const [notificationPermission, setNotificationPermission] = useState("default");
  // dynamic navigation
  const basePath = useBasePath();
  const navigate = useNavigate();
  const role =
    basePath === "Sadmin" || basePath === "Padmin" ? "Admin" : "Pharmacist";
  // function to navigate in buttons
  const handleToAddPharmacy = () => {
    navigate(`/${basePath}/pharmacies/AddPharmacy`);
  };
  const handleToAddPharmacist = () => {
    navigate(`/${basePath}/pharmacists/AddPharmacists`);
  };
  const handleToAddPatient = () => {
    navigate(`/${basePath}/patients/AddPatients`);
  };
  const handleToAddMedicines = () => {
    navigate(`/${basePath}/medicines/AddMedicines`);
  };

  // Function to handle enable notification button click
  const handleEnableNotifications = () => {
    if ('Notification' in window) {
      Notification.requestPermission().then(permission => {
        setNotificationPermission(permission);
        if (permission === 'granted') {
          message.success('Notifications enabled successfully!');
          new Notification('Notifications Enabled', {
            body: 'You will now receive notifications from FollowVita'
          });
        } else if (permission === 'denied') {
          message.error('Notification permission denied. Please enable notifications in your browser settings.');
        }
      });
    } else {
      message.error('Notifications are not supported in your browser.');
    }
  };
  useEffect(() => {
    if ('Notification' in window) {
      setNotificationPermission(Notification.permission);
    }

    const permissionInterval = setInterval(() => {
      if ('Notification' in window) {
        setNotificationPermission(Notification.permission);
      }
    }, 300000);

    return () => {
      clearInterval(permissionInterval);
    };
  }, []);

  const fetchLogs = async (pageNumber = 1, append = false) => {
    try {
      setLogsLoading(true);
      const limit = 10;
      const response = await axios.get(`${API_URL}logs/find-admin-logs?order=DESC&limit=${limit}&page=${pageNumber}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      const newLogs = response.data.AdminLogs || [];

      if (newLogs.length === 0 || newLogs.length < limit) {
        setHasMore(false);
      }

      if (append) {
        setActivityLogs(prevLogs => [...prevLogs, ...newLogs]);
      } else {
        setActivityLogs(newLogs);
      }

      setLogsLoading(false);
    } catch (error) {
      console.error("Error fetching activity logs:", error);
      message.destroy();
      message.error("Failed to fetch activity logs");
      setLogsLoading(false);
    }
  };

  const handleScroll = () => {
    if (logsContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = logsContainerRef.current;

      // Check if scrolled to bottom (with a small threshold)
      if (scrollHeight - scrollTop - clientHeight < 50 && !logsLoading && hasMore) {
        const nextPage = page + 1;
        setPage(nextPage);
        fetchLogs(nextPage, true);
      }
    }
  };

  const fetchStatistics = async () => {
    setIsLoading(true);
    try {
      let response;
      if (userType == 2 || userType == 4) {
        if (pharmacyId?.id) {
          response = await axios.get(
            `${API_URL}users/get-statistics?PharmacyID=${pharmacyId?.id}`,
            {
              headers: { Authorization: `Bearer ${accessToken}` },
            }
          );
        }
      } else if (userType == 1) {
        response = await axios.get(`${API_URL}users/get-statistics`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
      }
      setStatistics(response.data);
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching statistics:", error);
      message.destroy();
      // message.error("Failed to fetch statistics.");
    }
  };

  useEffect(() => {
    fetchStatistics();
    fetchLogs(1, false);
    setPage(1);
    setHasMore(true);
  }, [pharmacyId]); // 👈 Runs when `pharmacyId` changes

  const calculatePercentage = (part, whole) => {
    if (whole === 0) return 0; // Prevent division by zero
    return ((part / whole) * 100).toFixed(1);
  };

  return (
    <div className="flex flex-col gap-y-4 md:gap-y-[25px]">
      <h2 className="text-[#1C1C1C] font-semibold text-xl md:text-[32px]">
        Hello, {role}
      </h2>

      {notificationPermission !== "granted" && (
        <Alert
          message="Notifications are not enabled"
          description={
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <span>Enable notifications to receive important updates about your patients and prescriptions.</span>
              <Button type="primary" onClick={handleEnableNotifications}>
                Enable Notifications
              </Button>
            </div>
          }
          type="warning"
          showIcon
          closable
          className="mb-4"
        />
      )}

      {isLoading ? (
        <div className="flex justify-center items-center h-40">
          <Spin size="large" />
        </div>
      ) : (
        <>
          <div className="statistics-cards grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-[10px]" style={[2].includes(userType) ? { display: "flex", flexDirection: "row" } : {}}>
            {![4, 1].includes(userType) ? (
              <>
                <div className="w-full">
                  <StatCard
                    icon="/assets/images/wallet-2.svg"
                    title="My Patients"
                    number={Statistics.totalPatients}
                    percentage={null}
                  />
                </div>
                <div className="w-full">
                  <StatCard
                    icon="/assets/images/wallet-2.svg"
                    title="Active Patients"
                    number={Statistics.activePatients}
                    percentage={Math.round(
                      (Statistics.activePatients / Statistics.totalPatients) *
                      100
                    )}
                  />
                </div>
                <div className="w-full">
                  <StatCard
                    icon="/assets/images/wallet-2.svg"
                    title="Active Prescriptions"
                    number={Statistics.activePrescriptions}
                    percentage={Math.round(
                      (Statistics.ValidPrescribedMedicines /
                        (Statistics.ValidPrescribedMedicines +
                          Statistics.ExpiredPrescribedMedicines)) *
                      100
                    )}
                  />
                </div>
              </>
            ) : (
              <>
                <StatCard
                  icon="/assets/images/wallet-2.svg"
                  title="Total Patients"
                  number={Statistics.totalPatients}
                  percentage={null}
                />
                <StatCard
                  icon="/assets/images/wallet-2.svg"
                  title="Active Patients"
                  number={Statistics.activePatients}
                  percentage={calculatePercentage(
                    Statistics.activePatients,
                    Statistics.totalPatients
                  )}
                />
                <StatCard
                  icon="/assets/images/wallet-2.svg"
                  title="New Patients"
                  number={Statistics.newActivePatients}
                  percentage={calculatePercentage(
                    Statistics.newActivePatients,
                    Statistics.totalPatients
                  )}
                />
                <StatCard
                  icon="/assets/images/wallet-2.svg"
                  title="Active Pharmacists"
                  number={Statistics.pharmacistsCount}
                  percentage={calculatePercentage(
                    Statistics.pharmacistsCount,
                    Statistics.totalPharmacistsCount
                  )}
                />
              </>
            )}
          </div>
          <div className="flex flex-col md:flex-row justify-between gap-4 md:gap-0">
            <div className="default-border w-full md:w-[49%] bg-white p-4 md:p-5">
              {userType == 2 ? (
                <OverviewChart Statistics={Statistics} />
              ) : (
                <>
                  <div className="flex flex-col space-y-4 md:space-y-5">
                    {userType == 4 ? (
                      ""
                    ) : (
                      <div>
                        <div
                          onClick={handleToAddPharmacy}
                          className="text-lg md:text-[28px] font-medium w-full flex items-center border-0 cursor-pointer hover:shadow-md hover:bg-blue-50 transition-all duration-300"
                          style={{
                            backgroundColor: "#F7F8FF",
                            padding: "20px 20px",
                          }}
                        >
                          <img src="/assets/images/AddPharmacy.svg" className="w-16 md:w-auto mr-4" />
                          <span className="flex-1 text-center">
                            + Add Pharmacy
                          </span>
                        </div>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <div
                        onClick={handleToAddPharmacist}
                        className="text-lg md:text-[28px] font-medium w-full flex items-center border-0 cursor-pointer hover:shadow-md hover:bg-blue-50 transition-all duration-300"
                        style={{
                          backgroundColor: "#F7F8FF",
                          padding: "20px 20px",
                        }}
                      >
                        <img src="/assets/images/AddPharmacist.svg" className="w-16 md:w-[106px] mr-4" />
                        <span className="flex-1 text-center">
                          + Add Pharmacist
                        </span>
                      </div>
                    </div>
                    <div className="flex justify-between gap-4">
                      <div
                        onClick={handleToAddPatient}
                        className={`text-lg md:text-[28px] font-medium w-1/2 flex justify-start px-4 md:px-[10px] py-8 md:py-[10px] border-0 ${userType === 4 ? 'text-[19px]' : ''} cursor-pointer hover:shadow-md hover:bg-blue-50 transition-all duration-300`}
                        style={{
                          backgroundColor: "#F7F8FF",
                          fontSize: userType === 4 ? "32px" : "19px",
                          fontWeight: "500",
                          display: "flex",
                          border: "unset",
                          flexDirection: userType === 4 ? "row" : "column",
                          padding: "20px 20px",
                          rowGap: "10px",
                          width: userType === 4 ? "100%" : "",
                          textAlign: userType === 4 ? "center" : "",
                          alignItems: userType === 4 ? "center" : ""
                        }}
                      >
                        <img
                          src={"/assets/images/AddPatient.svg"}
                          className={userType === 4 ? "w-16 md:w-[106px] mr-4" : "w-20 h-20"}
                        />
                        <span className="flex-1 justify-center" style={{ fontSize: userType === 4 ? "32px" : "19px" }}>
                          + Add Patient
                        </span>
                      </div>

                      {userType == 4 ? (
                        ""
                      ) : (
                        <div
                          onClick={handleToAddMedicines}
                          className={`text-lg md:text-[28px] font-medium w-1/2 flex justify-start px-4 md:px-[10px] py-8 md:py-[10px] border-0 cursor-pointer hover:shadow-md hover:bg-blue-50 transition-all duration-300`}
                          style={{
                            backgroundColor: "#F7F8FF",
                            fontSize: "19px",
                            fontWeight: "500",
                            display: "flex",
                            border: "unset",
                            flexDirection: "column",
                            padding: "20px 20px",
                            rowGap: "10px"
                          }}
                        >
                          <img
                            src={"/assets/images/AddMedicines.svg"}
                            className="w-20 h-20"
                          />
                          <span className="flex-1 justify-center" style={{ fontSize: "19px" }}>
                            + Add Medicines
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="default-border w-full md:w-[49%] bg-white p-5">
              <h2 className="text-[20px] text-[#45464Es] font-semibold mb-5">
                Activity Log
              </h2>
              <div
                className="flex flex-col overflow-y-scroll max-h-[400px]"
                ref={logsContainerRef}
                onScroll={handleScroll}
              >
                {activityLogs.map(({ message, logger, createdAt }, i) => (
                  <ActivityLogItem
                    key={i}
                    logger={logger}
                    message={message}
                    date={new Date(createdAt).toISOString().slice(0, 10)}
                  />
                ))}
                {logsLoading && (
                  <div className="flex justify-center items-center py-3">
                    <Spin size="small" />
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;
